import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  X, Plus, CheckCircle, Tag, Sparkles, Send, Trash2, RefreshCw, Brain, Wand2, Pencil, Flag, XCircle, Layers,
  StickyNote, BookMarked, Lock, Unlock,
} from 'lucide-react';
import { UserStory, ExtractedPersona, Feature } from '../../services/workshop';
import { StringListEditor, SourceField, KeyedValue, toKeyed, fromKeyed } from './NotesEditor';
import { DeepDiveMessage, StoryAction } from '../../services/n8n';
import { api } from '../../services/api';
import { toast } from 'sonner';

interface StoryEditModalProps {
  story: UserStory;
  personas: { id: string; name: string }[];
  features?: Feature[];
  sessionId?: string;
  persona?: ExtractedPersona | null;
  createMode?: boolean;
  onSave: (storyId: string, patch: Partial<UserStory>) => Promise<void>;
  onClose: () => void;
}

const POLL_INTERVAL = 4000;

const makeMdComponents = (textColor: string): Components => ({
  p: ({ children }) => <p className={`text-sm leading-relaxed mb-2 last:mb-0 ${textColor}`}>{children}</p>,
  h1: ({ children }) => <h1 className="text-base font-bold text-slate-900 mt-3 mb-1.5">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold text-slate-800 mt-2.5 mb-1 pb-0.5 border-b border-slate-200">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-700 mt-2 mb-1">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className={`text-sm leading-relaxed ${textColor}`}>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <pre className="bg-slate-800 text-slate-100 rounded-lg p-3 my-2 overflow-x-auto text-xs"><code>{children}</code></pre>
    ) : (
      <code className="bg-slate-200/60 text-slate-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
    );
  },
  hr: () => <hr className="my-3 border-slate-200" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/40 pl-3 my-2 text-sm italic text-slate-600 bg-slate-50/60 py-1.5 rounded-r">{children}</blockquote>
  ),
});

const ACTION_CONFIG: Record<StoryAction['type'], { label: (a: StoryAction) => string; color: string; icon: React.ReactNode }> = {
  approve:      { label: () => 'Approve',                    color: 'border-green-200 text-green-700 hover:bg-green-50',    icon: <CheckCircle size={11} /> },
  reject:       { label: () => 'Reject',                     color: 'border-red-200 text-red-600 hover:bg-red-50',          icon: <XCircle size={11} /> },
  set_priority: { label: a => `Set priority: ${a.priority}`, color: 'border-amber-200 text-amber-700 hover:bg-amber-50',   icon: <Flag size={11} /> },
  add_ac:       { label: () => 'Add AC',                     color: 'border-blue-200 text-blue-700 hover:bg-blue-50',       icon: <Plus size={11} /> },
  reword:       { label: () => 'Apply suggestion',           color: 'border-purple-200 text-purple-700 hover:bg-purple-50', icon: <Pencil size={11} /> },
};

export function StoryEditModal({ story, personas, features, sessionId, persona, createMode, onSave, onClose }: StoryEditModalProps) {
  // Form state
  const [storyText, setStoryText] = useState(story.story);
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>(story.priority);
  const [personaName, setPersonaName] = useState(story.persona);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<KeyedValue[]>(toKeyed(story.acceptanceCriteria, 'ac'));
  const [notes, setNotes] = useState<KeyedValue[]>(toKeyed(story.notes, 'note'));
  const [source, setSource] = useState(story.source || '');
  const [tags, setTags] = useState<string[]>([...(story.tags || [])]);
  const [tagInput, setTagInput] = useState('');
  const [featureIds, setFeatureIds] = useState<string[]>([...(story.featureIds || [])]);
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  // AI chat state
  const [messages, setMessages] = useState<DeepDiveMessage[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const knownMessageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!sessionId || createMode) return;
    const load = async () => {
      try {
        const res = await api.get(`/api/sessions/${sessionId}/user-stories/${story.id}/refinement-chat/messages`);
        const msgs: DeepDiveMessage[] = res.data.messages || [];
        msgs.forEach(m => knownMessageIds.current.add(m.id));
        setMessages(msgs);
      } catch {}
    };
    load();
  }, [sessionId, story.id]);

  const pollMessages = useCallback(async () => {
    if (!sessionId || createMode) return;
    try {
      const res = await api.get(`/api/sessions/${sessionId}/user-stories/${story.id}/refinement-chat/messages`);
      const all: DeepDiveMessage[] = res.data.messages || [];
      const fresh = all.filter(m => !knownMessageIds.current.has(m.id));
      if (fresh.length > 0) {
        fresh.forEach(m => knownMessageIds.current.add(m.id));
        setMessages(prev => [...prev, ...fresh]);
        if (fresh.some(m => m.agentType === 'specialist')) setAnalyzing(false);
      }
    } catch {}
  }, [sessionId, story.id]);

  useEffect(() => {
    if (!sessionId || createMode) return;
    pollRef.current = setInterval(pollMessages, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId, createMode, pollMessages]);

  const handleAnalyze = useCallback(async () => {
    if (!sessionId) return;
    setAnalyzing(true);
    try {
      await api.post(`/api/sessions/${sessionId}/user-stories/${story.id}/refinement-chat/analyze`, {
        personaId: persona?.id,
        personaName: persona?.name,
        personaRole: persona?.role,
        personaDescription: persona?.description,
        storyText,
        // Unwrap before sending: the refinement workflow expects plain strings,
        // and api.post is untyped so a keyed array would go through silently.
        acceptanceCriteria: fromKeyed(acceptanceCriteria),
      });
    } catch {
      toast.error('Failed to start refinement');
      setAnalyzing(false);
    }
  }, [sessionId, story.id, persona, storyText, acceptanceCriteria]);

  const handleClearChat = useCallback(async () => {
    if (!sessionId) return;
    await api.delete(`/api/sessions/${sessionId}/user-stories/${story.id}/refinement-chat/messages`);
    setMessages([]);
    knownMessageIds.current.clear();
  }, [sessionId, story.id]);

  const handleUserMessage = useCallback(async () => {
    if (!userMessage.trim() || !sessionId) return;
    const text = userMessage.trim();
    setUserMessage('');
    setMessages(prev => [...prev, {
      id: `facilitator-${Date.now()}`,
      agentType: 'specialist',
      personaId: story.id,
      personaName: 'Facilitator',
      message: text,
      timestamp: new Date().toISOString(),
    }]);
    setAnalyzing(true);
    try {
      await api.post(`/api/sessions/${sessionId}/user-stories/${story.id}/refinement-chat/analyze`, {
        personaId: persona?.id,
        personaName: persona?.name,
        personaRole: persona?.role,
        personaDescription: persona?.description,
        storyText,
        // Unwrap before sending: the refinement workflow expects plain strings,
        // and api.post is untyped so a keyed array would go through silently.
        acceptanceCriteria: fromKeyed(acceptanceCriteria),
      });
    } catch {
      toast.error('Failed to send message');
      setAnalyzing(false);
    }
  }, [userMessage, sessionId, story.id, persona, storyText, acceptanceCriteria]);

  const handleAction = useCallback((action: StoryAction) => {
    if (isLocked) return;
    if (action.type === 'reword' && action.suggestion) {
      setStoryText(action.suggestion);
    } else if (action.type === 'set_priority' && action.priority) {
      setPriority(action.priority);
    } else if (action.type === 'add_ac' && action.criteria?.length) {
      setAcceptanceCriteria(prev => [...prev, ...toKeyed(action.criteria, 'ac-ai')]);
    }
  }, []);

  const handleSave = async () => {
    if (!storyText.trim() || isLocked) return;
    setSaving(true);
    try {
      await onSave(story.id, {
        story: storyText.trim(),
        priority,
        persona: personaName,
        acceptanceCriteria: fromKeyed(acceptanceCriteria),
        notes: fromKeyed(notes),
        source: source.trim(),
        tags,
        featureIds,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };
  const removeTag = (tag: string) => setTags(prev => prev.filter(t => t !== tag));
  const toggleFeature = (featureId: string) =>
    setFeatureIds(prev => prev.includes(featureId) ? prev.filter(f => f !== featureId) : [...prev, featureId]);

  const isClone = story.id.startsWith('CLONE-');
  const hasAiPanel = !!sessionId && !isClone && !createMode;
  const isLocked = !!story.locked && !isClone && !createMode;

  const handleUnlock = async () => {
    if (!sessionId) return;
    setUnlocking(true);
    try {
      await api.patch(`/api/sessions/${sessionId}/user-stories/${story.id}/unlock`, {});
      onClose();
    } finally {
      setUnlocking(false);
    }
  };

  const handleAvatarReview = async () => {
    if (!sessionId) return;
    try {
      const res = await api.post(`/api/sessions/${sessionId}/user-stories/avatar-review`, {
        scope: { type: 'story', storyId: story.id, persona: story.persona },
      });
      toast.success(`Asked ${res.data.avatarCount} avatar${res.data.avatarCount === 1 ? '' : 's'} to review this story`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to request avatar review');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${hasAiPanel ? 'max-w-5xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] flex-shrink-0">
          <h2 className="text-base font-bold text-navy">{createMode ? 'Add Story' : isClone ? 'Clone Story' : 'Edit Story'}</h2>
          <div className="flex items-center gap-2">
            {sessionId && !isClone && !createMode && (
              <button
                onClick={handleAvatarReview}
                className="flex items-center gap-1.5 rounded-xl h-8 px-3 border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors"
                title="Ask session avatars to review this story"
              >
                <Sparkles size={12} /> Ask avatars to review
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: form */}
          <div className={`${hasAiPanel ? 'flex-1 border-r border-[#e2e8f0]' : 'flex-1'} overflow-y-auto p-6 flex flex-col gap-5`}>
            {isLocked && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                <Lock size={13} className="shrink-0" />
                This story is locked{story.lockedBy ? ` by ${story.lockedBy}` : ''} and cannot be edited until unlocked.
              </div>
            )}
            <fieldset disabled={isLocked} className="contents">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Story</label>
              <textarea
                value={storyText}
                onChange={e => setStoryText(e.target.value)}
                rows={4}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
                <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                  {(['high', 'medium', 'low'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${
                        priority === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Persona</label>
                <select
                  value={personaName}
                  onChange={e => setPersonaName(e.target.value)}
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                >
                  {personas.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                  {!personas.find(p => p.name === personaName) && (
                    <option value={personaName}>{personaName}</option>
                  )}
                </select>
              </div>
            </div>

            {!isClone && (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Story ID</label>
                <span className="inline-block px-2.5 py-1 rounded-lg bg-slate-100 text-xs font-mono text-slate-500">{story.id}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Tag size={11} />Tags
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Type a tag and press Enter"
                  className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  onClick={addTag}
                  disabled={!tagInput.trim()}
                  className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold transition-colors disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            </div>

            <StringListEditor
              label="Acceptance Criteria"
              icon={<CheckCircle size={11} />}
              items={acceptanceCriteria}
              onChange={setAcceptanceCriteria}
              idPrefix="ac"
              addLabel="Add criterion"
              placeholder="A condition that must be true for this story to be done"
            />

            <StringListEditor
              label="Notes"
              icon={<StickyNote size={11} />}
              items={notes}
              onChange={setNotes}
              idPrefix="note"
              addLabel="Add note"
              multiline
              placeholder="Working note or commentary"
              emptyHint="Commentary that is not a condition of done. Questions, risks and blockers belong in the RAID register, not here."
            />

            <SourceField value={source} onChange={setSource} icon={<BookMarked size={11} />} />

            {features && features.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Layers size={11} />Features
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {features.map(f => {
                    const selected = featureIds.includes(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => toggleFeature(f.id)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          selected
                            ? 'bg-primary/10 border-primary/40 text-primary'
                            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {f.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            </fieldset>
          </div>

          {/* Right: AI refinement chat */}
          {hasAiPanel && (
            <div className="w-[360px] flex flex-col flex-shrink-0 bg-slate-50/30">
              {/* AI panel header */}
              <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2.5 flex-shrink-0 bg-white">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 text-purple-600 flex-shrink-0">
                  <Wand2 size={13} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-navy leading-tight">AI Refinement</p>
                  <p className="text-[10px] text-slate-400 truncate">UX Specialist will suggest rewrites</p>
                </div>
              </div>

              {/* Chat thread */}
              <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 min-h-0">
                {messages.length === 0 && !analyzing && (
                  <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-8">
                    <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <Brain size={16} className="text-blue-500" />
                    </div>
                    <p className="text-slate-500 text-xs">UX Specialist ready to refine</p>
                    <button
                      onClick={handleAnalyze}
                      className="flex items-center gap-1.5 rounded-xl h-8 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm"
                    >
                      <Sparkles size={12} /> Suggest improvements
                    </button>
                  </div>
                )}

                {messages.map(msg => {
                  const isFacilitator = msg.personaName === 'Facilitator';
                  if (isFacilitator) {
                    return (
                      <div key={msg.id} className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy text-white px-3 py-2">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeMdComponents('text-white')}>
                            {msg.message}
                          </ReactMarkdown>
                        </div>
                      </div>
                    );
                  }
                  const hasActions = msg.actions && msg.actions.length > 0;
                  return (
                    <div key={msg.id} className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-50 border border-blue-200">
                        <Brain size={11} className="text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[10px] font-bold tracking-wide text-blue-600">UX Specialist</span>
                          <span className="text-[10px] text-slate-400">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="rounded-2xl rounded-tl-sm px-3 py-2 bg-blue-50 border border-blue-100">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeMdComponents('text-slate-700')}>
                            {msg.message}
                          </ReactMarkdown>
                        </div>
                        {hasActions && (
                          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold">
                              <Wand2 size={9} /> Actions
                            </div>
                            {msg.actions!.map((action, i) => {
                              const cfg = ACTION_CONFIG[action.type];
                              return (
                                <button
                                  key={`${action.type}-${action.storyId}`}
                                  onClick={() => handleAction(action)}
                                  disabled={isLocked}
                                  title={isLocked ? 'Story is locked — unlock to apply' : undefined}
                                  className={`flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-semibold transition-colors bg-white disabled:opacity-40 disabled:cursor-not-allowed ${cfg.color}`}
                                >
                                  {cfg.icon}
                                  {cfg.label(action)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {analyzing && (
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-[11px] text-slate-400">Specialist thinking…</span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat input */}
              <div className="border-t border-[#e2e8f0] px-3 py-2.5 bg-white flex-shrink-0">
                <div className="flex items-center gap-1.5 mb-2">
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="flex items-center gap-1 rounded-xl h-7 px-2.5 border border-primary/30 text-primary text-[11px] font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={10} className={analyzing ? 'animate-spin' : ''} />
                    Re-analyse
                  </button>
                  <button
                    onClick={handleClearChat}
                    className="flex items-center gap-1 rounded-xl h-7 px-2.5 border border-slate-200 text-slate-500 text-[11px] font-semibold hover:bg-slate-50 transition-colors"
                  >
                    <Trash2 size={10} /> Clear
                  </button>
                </div>
                <div className="flex gap-1.5">
                  <input
                    value={userMessage}
                    onChange={e => setUserMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUserMessage(); } }}
                    placeholder="Ask for specific improvements…"
                    className="flex-1 border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <button
                    onClick={handleUserMessage}
                    disabled={!userMessage.trim() || analyzing}
                    className="flex items-center justify-center w-8 h-8 rounded-xl bg-primary hover:bg-primary-dark transition-colors text-white disabled:opacity-50"
                  >
                    <Send size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          {isLocked ? (
            <button
              onClick={handleUnlock}
              disabled={unlocking || !sessionId}
              className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-amber-600 hover:bg-amber-700 transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              <Unlock size={14} />
              {unlocking ? 'Unlocking…' : 'Unlock to edit'}
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving || !storyText.trim()}
              className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              {saving ? 'Saving…' : createMode ? 'Add' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
